import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import profile from 'src/store/profile';
import api from 'src/utils/api';
// @mui
import { styled } from '@mui/material/styles';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Grid,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
  Card,
  CardContent,
  
  Link,
  Snackbar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { CreateFeedback, CreateMessage } from 'src/sections/@dashboard/student';
import { OneMessageNews } from 'src/sections/@dashboard/app';
import Resizer from 'react-image-file-resizer';
import { fToNow } from '../../utils/formatTime';
// components
import Iconify from '../../components/iconify';

// ----------------------------------------------------------------------

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function ProfilePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [openMessage, setOpenMessage] = useState(false);
  const [alert, setAlert] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [messages, setMessages] = useState([]);
  const [student] = useState(profile.isTeacher ? location.state : profile.data);
  const [image, setImage] = useState(profile.avatar);
  // recommendation states (teacher -> recommend extra lesson to student)
  const [openRecommend, setOpenRecommend] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState('');
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [tab, setTab] = useState(1);
  // recommended lessons (shown to student and teacher)
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);

  const getMessages = useCallback(() => {
    setIsOpen(false);
    api.get(`message-api?expand=lesson&sort=-id&filter[student_id]=${student.id}&filter[or][0][type]=0&filter[or][1][type]=5`)
      .then((result) => {
        setMessages(result);
      })
      .catch(() => {
        setMessages([]);
      });
  }, [student.id]);

    const getLessons = useCallback(() => {
      setLessonsLoading(true);
      // Fetch courses first (so we can select by course)
      api.get('course-api')
        .then((result) => {
          const courseList = Array.isArray(result) ? result : result?.data || [];
          setCourses(courseList);

          // Determine effective course id (don't overwrite if user already selected one)
          let effectiveCourseId = courseId;
          if (!effectiveCourseId) {
            // prefer course from profile if present
            if (profile.data?.course_id) effectiveCourseId = profile.data.course_id;

            // then fallback to student.course_id
            if (!effectiveCourseId && student && student.course_id) effectiveCourseId = student.course_id;

            // finally, fallback to first available course
            if (!effectiveCourseId && courseList && courseList.length > 0) effectiveCourseId = courseList[0].id;

            if (effectiveCourseId) setCourseId(effectiveCourseId);
          }

          // If we don't have a course id yet, skip fetching lessons (it will run again when courseId is set)
          const cid = effectiveCourseId || courseId;
          if (!cid) {
            setLessonsLoading(false);
            return;
          }

          // Fetch lessons for the selected course and edmap (tab)
          const lessonPath = `lesson-api?sort=name&filter[edmap]=${tab}&filter[course_id]=${cid}&per-page=100`;
          api.get(lessonPath)
            .then((resLessons) => {
              const data = Array.isArray(resLessons) ? resLessons : resLessons?.data || [];
              // keep only level === 2 (intensive) lessons for recommendation
              const filtered = (data || []).filter((l) => Number(l.level) === 2);
              setLessons(filtered);
            })
            .catch(() => {
              setLessons([]);
            })
            .finally(() => setLessonsLoading(false));
        })
        .catch(() => {
          setLessonsLoading(false);
        });
    }, [courseId, tab, student]);

    const submitRecommendation = () => {
      if (!selectedLesson) return;

      const payload = {
        student_id: student.id,
        lesson_id: selectedLesson,
        studio_id: profile.data.studio_id || 1,
      };

      api.post('recommended-api', payload)
        .then(() => {
            // success
            setOpenRecommend(false);
            setAlert(true);
            setSelectedLesson('');
          })
          .catch((err) => {
            api.logError('recommendation failed', err);
            setSnackbar({ open: true, message: err?.message || 'Не вдалося відправити рекомендацію', severity: 'error' });
          });
    };

  const saveAvatar = (img) => {
    const storedJson = JSON.stringify(img);
    localStorage.setItem('Avatar', storedJson);
    profile.setAvatar(img);
  };

  const readAvatar = () => {
    const storedJson = JSON.parse(localStorage.getItem('Avatar'));
    if (storedJson !== undefined) {
      setImage(storedJson);
      profile.setAvatar(storedJson);
    }
  };

  useEffect(() => {
    readAvatar();
    getMessages();
  }, [getMessages]);

  // Fetch recommended lessons for this student (same logic as CalendarPage)
  useEffect(() => {
    if (!student || !student.id) return;

    const urlPath = `recommended-api?expand=lesson&filter[student_id]=${student.id}&filter[status]=0&per-page=2`;

    setLoadingRecommended(true);
    api.get(urlPath)
      .then((result) => {
        // normalize array vs object
        if (Array.isArray(result)) {
          setRecommended(result.map(item => ({
            ...item,
            title: item.lesson && item.lesson.name ? item.lesson.name : (item.text && item.text.trim() ? item.text : `Урок #${item.lesson_id}`),
            description: item.lesson && item.lesson.info ? item.lesson.info : '',
            dateText: item.create_date ? fToNow(item.create_date) : '',
            requested: item.status && Number(item.status) !== 0,
          })));
        } else if (result) {
          const item = result;
          setRecommended([{
            ...item,
            title: item.lesson && item.lesson.name ? item.lesson.name : (item.text && item.text.trim() ? item.text : `Урок #${item.lesson_id}`),
            description: item.lesson && item.lesson.info ? item.lesson.info : '',
            dateText: item.create_date ? fToNow(item.create_date) : '',
            requested: item.status && Number(item.status) !== 0,
          }]);
        }
      })
      .catch(() => {
        setRecommended([]);
      })
      .finally(() => setLoadingRecommended(false));
  }, [student]);

  // Handle student clicking "Хочу" on a recommended item
  const handleWant = (rec) => {
    setRecommended(prev => prev.map(r => r.id === rec.id ? { ...r, requested: true } : r));

    const payload = { status: 1 };

    api.patch(`recommended-api/${rec.id}`, payload)
      .then(() => {
      })
      .catch(err => {
        api.logError('status update failed', err);
        // rollback optimistic update
        setRecommended(prev => prev.map(r => r.id === rec.id ? { ...r, requested: false } : r));
        setSnackbar({ open: true, message: err?.message || 'Не вдалося оновити статус', severity: 'error' });
      });
  };

  useEffect(() => {
    if (openRecommend) {
      getLessons();
    }
  }, [openRecommend, courseId, tab, getLessons]);

  const resizeFile = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        300,
        300,
        'JPEG',
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        'base64'
      );
    });

  const changeHandler = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const img = await resizeFile(file);
      setImage(img);
      saveAvatar(img);
    }
  };

  return (
    <>
      <Helmet>
        <title> Профіль | Lala English </title>
      </Helmet>

      <Alert severity="error" sx={{ mb: 5, pl: 3 }}>Твій центр керування.</Alert>
      <Container maxWidth="xl">
        {profile.isTeacher ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Button
              onClick={() => navigate(-1)}
              variant="contained"
              startIcon={<Iconify icon="eva:arrow-back-fill" />}
              sx={{
                backgroundColor: 'common.white',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.main',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              Назад
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setOpenMessage(true)}
                variant="contained"
                startIcon={<Iconify icon="eva:email-fill" />}
                sx={{
                  backgroundColor: 'common.white',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                  },
                }}
              >
                Повідомлення
              </Button>
              <Button
                onClick={() => setOpenRecommend(true)}
                variant="contained"
                startIcon={<Iconify icon="eva:award-fill" />}
                sx={{
                  backgroundColor: 'common.white',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                  },
                }}
              >
                Рекомендувати урок
              </Button>
            </Stack>
          </Stack>
        ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <Card 
              sx={{ 
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <CardContent sx={{ pt: 3 }}>
                <Stack alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar 
                      sx={{ 
                        width: 140, 
                        height: 140, 
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                      }} 
                      src={image} 
                    />
                    <IconButton
                      component="label"
                      sx={{
                        position: 'absolute',
                        bottom: -8,
                        right: -8,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        width: 40,
                        height: 40,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        }
                      }}
                    >
                      <Iconify icon="eva:camera-fill" width={20} />
                      <VisuallyHiddenInput type="file" onChange={changeHandler} accept="image/*" />
                    </IconButton>
                  </Box>
                </Stack>

                <Typography variant="h6" align="center" sx={{ mb: 1, fontWeight: 600 }}>
                  {student.name}
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 3 }}>
                  Студент
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'success.main'
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                    Активний
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  {!profile.isTeacher ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Iconify icon="eva:book-outline" sx={{ color: 'primary.main' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                       Курс: {profile.data.course ? `${profile.data.course.name} ${profile.data.course.level === 0 ? 'Base' : 'Classic'}` : 'Поки-що ви не записані на жоден курс'}
                      </Typography>
                    </Box>
                  ) : null}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Iconify icon="eva:calendar-outline" sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Особиста траекторія:
                    </Typography>
                    <Link 
                      component="button" 
                      onClick={() => navigate('/student/calendar')} 
                      variant="body2"
                      sx={{ 
                        textDecoration: 'none',
                        fontWeight: 500,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      click here
                    </Link>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="eva:email-outline" sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {student.email}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="eva:phone-outline" sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {student.phone}
                    </Typography>
                  </Box>
                  
                  
                  </Stack>

                {profile.isTeacher ? (
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                      onClick={() => setIsOpen(true)}
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<Iconify icon="eva:message-square-fill" />}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.5
                      }}
                    >
                      Залишити фідбек батькам
                    </Button>
                  </Box>
                ) : null}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
            {!profile.isTeacher && profile.data.course ? (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'info.light',
                  '& .MuiAlert-icon': {
                    fontSize: '1.2rem'
                  }
                }}
              >
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {profile.data.course.text}
                </Typography>
              </Alert>
            ) : null}

            {/* Виконані завдання */}
            <OneMessageNews
              title="Виконані завдання"
              list={messages
                .filter(m => m.lesson !== undefined && m.lesson !== null && m.lesson !== "")
                .map((message) => ({
                  id: message.id,
                  title: 'Виконане завдання',
                  description: message.text,
                  image,
                  postedAt: message.date,
                  link: message.link,
                  lesson: message.lesson
                }))}
            />

            {/* Recommended lessons block */}
            {(loadingRecommended || (recommended && recommended.length > 0)) && (
              <Box sx={{ width: '100%', mt: 3 }}>
                {loadingRecommended ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {recommended.slice(0, 2).map((rec) => (
                      <Grid item xs={12} md={6} key={rec.id}>
                        <Box
                          sx={(theme) => ({
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            p: 3,
                            gap: 2,
                            minHeight: 260, // increase vertical space so full text fits
                            border: `1px solid ${
                              rec.requested ? theme.palette.success.main : theme.palette.divider
                            }`,
                            boxShadow: rec.requested
                              ? `0 0 0 4px ${theme.palette.success[50]}`
                              : 'none',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            transition: 'box-shadow 0.3s',
                            '&:hover': {
                              boxShadow: theme.shadows[4]
                            }
                          })}
                        >
                          {/* Header: title on left, button on the same row on the right */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ flex: 1, pr: 1 }}>
                              {rec.title ? (
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                  {rec.title}
                                </Typography>
                              ) : null}
                              {rec.dateText ? (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
                                  {rec.dateText}
                                </Typography>
                              ) : null}
                            </Box>

                            <Box sx={{ ml: 1, flexShrink: 0 }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleWant(rec)}
                                disabled={Boolean(
                                  rec.requested || (rec.status && Number(rec.status) !== 0)
                                )}
                                sx={(theme) => ({
                                  width: 100,
                                  minWidth: 100,
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  bgcolor: rec.requested
                                    ? theme.palette.success.main
                                    : theme.palette.warning.main,
                                  color: '#fff',
                                  '&:hover': {
                                    bgcolor: rec.requested
                                      ? theme.palette.success.dark
                                      : theme.palette.warning.dark,
                                  },
                                  boxShadow: 'none',
                                })}
                              >
                                {rec.requested ? 'Доданий' : 'Хочу'}
                              </Button>
                            </Box>
                          </Box>

                          {/* Description below header: full width */}
                          {rec.description ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mt: 1.5,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}
                            >
                              {rec.description}
                            </Typography>
                          ) : null}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Grid>
          <Snackbar open={alert} autoHideDuration={4000} onClose={() => setAlert(false)}>
            <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
              Успішно відправлено!
            </Alert>
          </Snackbar>
          <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Grid>
        
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Dialog onClose={() => setIsOpen(false)} open={isOpen}>
            <DialogTitle>
              {' '}
              <Typography>Фідбек батькам</Typography>
            </DialogTitle>
            <DialogContent>
              <CreateFeedback
                student={student}
                success={() => {
                  setIsOpen(false);
                  setAlert(true);
                }}
              />
            </DialogContent>
          </Dialog>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Dialog onClose={() => setOpenMessage(false)} open={openMessage}>
            <DialogTitle>
              <Typography>Надіслати повідомлення</Typography>
            </DialogTitle>
            <DialogContent>
              <CreateMessage
                type={0}
                student={student}
                success={() => {
                  getMessages();
                  setOpenMessage(false);
                  setAlert(true);
                }}
              />
            </DialogContent>
          </Dialog>
        </Stack>
        <Dialog onClose={() => setOpenRecommend(false)} open={openRecommend} fullWidth maxWidth="sm">
          <DialogTitle>
            <Typography>Рекомендувати урок</Typography>
          </DialogTitle>
          <DialogContent>
            {/* Вибір курсу (за аналогією з ProductsPage) */}
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel id="select-course-label">Курс</InputLabel>
              <Select
                labelId="select-course-label"
                value={courseId || ''}
                label="Курс"
                onChange={(e) => setCourseId(e.target.value)}
              >
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name} {c.level ? 'Classic' : 'Base'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* EdMap переключателі */}
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <ToggleButtonGroup
                value={tab}
                exclusive
                onChange={(_, value) => {
                  // якщо value === null — нічого не міняємо
                  if (value !== null) setTab(value);
                }}
              >
                <ToggleButton value={1}>Ed Map 1</ToggleButton>
                <ToggleButton value={2}>Ed Map 2</ToggleButton>
                <ToggleButton value={3}>Ed Map 3</ToggleButton>
                <ToggleButton value={4}>Ed Map 4</ToggleButton>
                <ToggleButton value={5}>Ed Map 5</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {/* Селект уроку (відфільтрований за курсом та edmap) */}
            {lessonsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                <CircularProgress />
              </Box>
            ) : lessons.length === 0 ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                Немає уроків для рекомендації - змініть курс або едмапу
              </Typography>
            ) : (
              <FormControl fullWidth sx={{ mt: 0, mb: 2 }}>
                <InputLabel id="select-lesson-label">Урок</InputLabel>
                <Select
                  labelId="select-lesson-label"
                  value={selectedLesson}
                  label="Урок"
                  onChange={(e) => setSelectedLesson(e.target.value)}
                >
                  {lessons.map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setOpenRecommend(false)}>Відмінити</Button>
              <Button disabled={lessonsLoading || !selectedLesson} variant="contained" onClick={submitRecommendation}>
                Відправити
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
}